/**
 * ENG-5641 — host-side connectivity probe executor (manager CLI).
 *
 * Given an installed integration, resolves its probe strategy (shared core
 * resolver) and executes the corresponding read-only probe, returning a
 * ConnectivityProbeOutcome to report up via POST /host/integration-connectivity.
 *
 * Runs in the manager because that is where the agent's real credentials,
 * network egress and live MCP servers are — the only vantage point that can
 * honestly reach all four source types.
 *
 * PURE + INJECTABLE: every side-effecting capability (HTTP, CLI exec, the MCP
 * handshake, the Composio account check) is injected, so dispatch + outcome
 * mapping is unit-testable without a live host. The real implementations of
 * `mcpProbe` (a true MCP `initialize → tools/list` client), `runCli` and
 * `composioProbe` are wired into the poll loop separately and exercised against
 * live hosts — this module owns the routing and the read-only invariant.
 *
 * Returns `null` when there is no probe to run (kind 'unsupported', or an
 * injectable capability the caller didn't supply yet) — callers skip reporting
 * rather than inventing a status.
 */

import {
  resolveConnectivityProbe,
  probeHttpProvider,
  worseConnectivityOutcome,
  bestConnectivityEvidence,
  type ConnectivityEvidence,
  type ConnectivityProbeOutcome,
  type ConnectivityTestOverride,
  type ToolkitSourceType,
} from '@augmented/core/integrations';
import type { IntegrationAuthType } from '@augmented/core';

export interface McpProbeTarget {
  /** Server key / name as wired in the agent's .mcp.json. */
  serverKey: string;
  definitionId: string;
  /**
   * ENG-8049: an inline `.mcp.json`-shaped server entry to probe INSTEAD of
   * looking `serverKey` up in the agent's `.mcp.json`. Set for a quarantined
   * integration, which the quarantine removed from `.mcp.json` - the manager
   * reconstructs its server entry via the framework adapter and passes it here so
   * recovery can be observed. Absent ⇒ the probe reads `.mcp.json` as before.
   * Typed as `unknown` to avoid a cross-module type dependency; the probe deps
   * that consume it (connectivity-probe-context) narrow it to McpJsonServerEntry.
   */
  inlineServerEntry?: unknown;
  /**
   * ENG-6242 — for the managed tool-call leg: the operator-stored
   * `connectivity_test.tool` to call (e.g. `LINEAR_GET_CURRENT_USER`) instead of
   * letting the probe auto-pick. Carried VERBATIM from the toolkit override; the
   * tool-call probe re-validates it against the live tools/list and falls back to
   * the heuristic on drift, so this only ever CHOOSES which safe tool runs.
   */
  toolName?: string | null;
  /** ENG-6242 — args for {@link toolName} (default `{}`). Ignored unless the override is used. */
  toolArgs?: Record<string, unknown> | null;
  /**
   * ENG-8363 — the integration row's `created_at`, so a leg can tell "the
   * `.mcp.json` key is missing because the writer has not run yet" from "the key
   * is missing and never will be". The manager probes BEFORE it writes
   * `.mcp.json` in the same poll, so the first probe of every newly-added
   * integration finds its key absent; without an age the leg cannot avoid
   * calling that a hard `down`. Absent/unparseable ⇒ treated as still inside the
   * first-connect window (fail-open — never escalate without evidence of age).
   */
  createdAt?: string | null;
  /**
   * ENG-9718 — the row's `reconnect_quiesced_until` deadline. A reconnect
   * returns the row to the set `/host/agent-integrations` forwards, so its key
   * is absent from `.mcp.json` until the host's next poll writes it — the same
   * condition {@link createdAt} covers for a NEW row, reached by a different
   * history. Without it, a row created months ago and reconnected seconds ago
   * reads as old and its absent key grades a hard `down`.
   */
  reconnectQuiescedUntil?: string | null;
  /**
   * ENG-8363 — this row is ENG-7916-quarantined, so its absence from `.mcp.json`
   * is the PRECONDITION of the probe (ENG-8036 re-probes quarantined rows purely
   * to observe recovery), not a fault. Never escalates to `down`.
   */
  expectedAbsent?: boolean;
}

export interface ConnectivityProbeDeps {
  /** Injected fetch for the HTTP-provider probes (defaults to global fetch). */
  fetchImpl?: typeof fetch;
  /** Run a read-only CLI command (e.g. `<bin> --version`); resolves an outcome. */
  runCli?: (binary: string, args: string[]) => Promise<ConnectivityProbeOutcome>;
  /** Real MCP client: initialize → tools/list against the agent's wired server. */
  mcpProbe?: (target: McpProbeTarget) => Promise<ConnectivityProbeOutcome>;
  /**
   * ENG-7405: local-STDIO MCP probe — spawns the toolkit's bundled server with
   * the agent's env, handshakes, and (when `target.toolName` is set) calls that
   * read-only tool. The stdio sibling of `mcpProbe`; the manager can't reach the
   * live session server's pipes, so it spawns a throwaway with the same env.
   */
  mcpStdioProbe?: (target: McpProbeTarget) => Promise<ConnectivityProbeOutcome>;
  /**
   * Composio connected-account check (account ACTIVE + bound to the runtime
   * user_id). Takes the resolved `.mcp.json` server key (ENG-7543:
   * connection-suffixed for a named connection) so the binding check reads the
   * exact server the agent queries, not a toolkit-level re-derivation.
   */
  composioProbe?: (
    serverKey: string,
    credentials: Record<string, unknown>,
    // ENG-8049: inline `.mcp.json`-shaped entry for a quarantined integration
    // (absent from `.mcp.json`), so the account-binding leg can read the same
    // url + x-api-key it would from the file. Absent ⇒ read `.mcp.json`.
    inlineServerEntry?: unknown,
    // ENG-8363: row facts this leg needs to classify an unreadable config the
    // same way the MCP legs do. Passed as a bag rather than more positionals
    // because this signature is already three deep. See McpProbeTarget for what
    // each means.
    rowCtx?: { createdAt?: string | null; reconnectQuiescedUntil?: string | null; expectedAbsent?: boolean },
  ) => Promise<ConnectivityProbeOutcome>;
  /**
   * ENG-6157 (Phase 2): live read-only tool call through the agent's wired MCP
   * server — the only leg that proves tool calls actually resolve the connected
   * account end-to-end. Returns `null` when there's no safe tool to call (skip).
   */
  composioToolCallProbe?: (target: McpProbeTarget) => Promise<ConnectivityProbeOutcome | null>;
}

export interface ConnectivityProbeTarget {
  definitionId: string;
  sourceType?: ToolkitSourceType | null;
  authType?: IntegrationAuthType | null;
  /** Decrypted credentials (the manager already holds these from /host/agent-integrations). */
  credentials: Record<string, unknown>;
  /** CLI binary name on PATH, when this is a cli_tool integration. */
  cliBinary?: string;
  /** MCP server key in the agent's .mcp.json, when this is an mcp_server integration. */
  mcpServerKey?: string;
  /**
   * ENG-7077 — the catalog `integration_definitions.remote_mcp.url` for a
   * hosted-remote-MCP integration (ADR-0033: monday, peec, anchor-browser),
   * forwarded by `/host/agent-integrations`. Without it the resolver cannot tell
   * these from any other `native` toolkit and returns `builtin`, i.e. a hardcoded
   * `unverified` with no network call — so the row can neither report itself
   * broken nor escalate, which is how a token revoked months ago kept reading
   * exactly like a healthy one.
   */
  remoteMcpUrl?: string | null;
  /**
   * ENG-6242 — the toolkit's `connectivity_test` override, when set. For managed
   * (Composio) toolkits this pins the tool-call leg to the SAME read-only tool the
   * central `POST /integrations/:id/test` path uses (e.g. `LINEAR_GET_CURRENT_USER`),
   * so the hourly probe and the Test button can't disagree. Null/absent ⇒ the
   * tool-call leg auto-picks a safe read-only tool (the pre-ENG-6242 behaviour).
   */
  connectivityTest?: ConnectivityTestOverride | null;
  /**
   * ENG-8049: for a quarantined integration (removed from `.mcp.json`), the
   * inline `.mcp.json`-shaped server entry the manager reconstructed via the
   * framework adapter. Threaded into every MCP leg so recovery can be observed
   * despite the missing file entry. Absent ⇒ the probe reads `.mcp.json`.
   * Opaque (`unknown`) here; the probe deps narrow it to McpJsonServerEntry.
   */
  inlineServerEntry?: unknown;
  /**
   * ENG-8363 — the row's `created_at`, forwarded by `/host/agent-integrations`
   * (added for ENG-7575). Threaded into every MCP leg so a missing `.mcp.json`
   * key can be read as "not written yet" rather than "never will be". See
   * {@link McpProbeTarget.createdAt}.
   */
  /**
   * ENG-9588 — REQUIRED, not optional, and that is the structural half of the
   * fix. These two fields are the only thing that makes the `down` /
   * "not wired in .mcp.json" verdict reachable, and an omitted `createdAt`
   * fails OPEN by design, so forgetting them is silent: the probe keeps
   * answering, plausibly, "still settling". The forced lane forgot them for
   * months while the scheduled lane set them, and nothing anywhere said so.
   *
   * Optionality is what made that possible. There are exactly two construction
   * sites (`connectivity-probe-runner`, `commands/integration`), so requiring
   * these costs one line each and turns "both lanes agree" into a compile
   * error rather than a property of whoever edits next. That is the same
   * omission the ENG-5463 and ENG-7077 comments in `commands/integration.ts`
   * record; this is the third, and the type is the only guard that scales to a
   * fourth.
   *
   * `null` remains a legitimate value for `createdAt` (row age genuinely
   * unknown, fail open); what is no longer legitimate is not saying.
   */
  createdAt: string | null;
  /**
   * ENG-9718 — the reconnect settling deadline; see
   * {@link McpProbeTarget.reconnectQuiescedUntil}.
   *
   * REQUIRED for the ENG-9588 reason recorded above, and the reasoning applies
   * with one inversion worth stating: this field fails CLOSED (absent ⇒ no
   * window ⇒ ordinary grading), so forgetting it does not manufacture a false
   * green — it silently restores the exact `down`-on-a-healthy-fleet bug
   * ENG-9718 exists to remove. Either direction of drift is invisible at
   * runtime, which is why it is a compile error instead.
   */
  reconnectQuiescedUntil: string | null;
  /** ENG-8363 — ENG-7916-quarantined row; see {@link McpProbeTarget.expectedAbsent}. */
  expectedAbsent: boolean;
}

/**
 * Execute the connectivity probe for one integration. Returns the outcome, or
 * `null` when no probe is available / wired (caller should not report a status).
 */
export async function executeConnectivityProbe(
  target: ConnectivityProbeTarget,
  deps: ConnectivityProbeDeps = {},
): Promise<ConnectivityProbeOutcome | null> {
  const descriptor = resolveConnectivityProbe({
    definitionId: target.definitionId,
    sourceType: target.sourceType,
    authType: target.authType,
    // ENG-6242: carry the toolkit's connectivity_test override so a managed
    // descriptor surfaces `probeTool`/`probeArgs` (the prescribed read-only tool)
    // — without this the hourly probe auto-picked a different tool than the Test
    // path, the false-RED that flagged every managed Linear install "unreachable".
    connectivityTest: target.connectivityTest ?? null,
    // ENG-7077: without this a static-token hosted remote MCP resolves to
    // `builtin` and reports `unverified` having contacted nothing.
    remoteMcpUrl: target.remoteMcpUrl ?? null,
    // ENG-5463: same omission one lane over. The target has carried
    // `cliBinary` all along (the runner reads it off the catalog row) and the
    // `cli_command` leg below spends it, but the RESOLVER never saw it — so a
    // toolkit seeded `native` with a CLI binary resolved to `builtin` and
    // reported `unverified` without running the CLI it is defined by.
    cliBinary: target.cliBinary ?? null,
  });

  // The resolver only ever returns read-only strategies; assert it so a future
  // mistake fails loud rather than letting a mutating probe through.
  if (!descriptor.readOnly) {
    throw new Error(`Refusing non-read-only probe for ${target.definitionId}`);
  }

  // ENG-8363: the row facts every MCP leg needs to classify an unreadable
  // config. Built ONCE and spread into each leg rather than repeated per call
  // site — there are five, and a leg that silently missed one of these would
  // fall back to the fail-open default and never escalate, which is exactly the
  // kind of one-sided drift that makes a probe read a meaningless green.
  const rowFacts = {
    createdAt: target.createdAt ?? null,
    // ENG-9718: the second settling anchor, carried in the same bag for the same
    // reason — five call sites, and a leg that missed it would fall back to
    // ordinary grading and brand a just-reconnected row `down`.
    reconnectQuiescedUntil: target.reconnectQuiescedUntil ?? null,
    expectedAbsent: target.expectedAbsent ?? false,
  } as const;

  switch (descriptor.kind) {
    case 'http_provider': {
      // null → unknown provider; surface as "no probe" rather than a fake status.
      const outcome = await probeHttpProvider(target.definitionId, target.credentials, deps.fetchImpl ?? fetch);
      // ENG-8226: an HTTP provider probe is an authenticated request against the
      // provider with the agent's own credentials (Linear's viewer query, Xero's
      // connections read). That IS a live call.
      return outcome ? withEvidence(outcome, 'live_call') : outcome;
    }

    case 'composio_account': {
      if (!deps.composioProbe) return null;
      const outcome = await deps.composioProbe(
        target.mcpServerKey ?? target.definitionId,
        target.credentials,
        target.inlineServerEntry,
        rowFacts,
      );
      // ENG-8226: this leg reads Composio's stored connected-account record. It
      // proves the record says ACTIVE, not that the grant behind it still works
      // — exactly the "record-only check, no live call" ENG-6328 identified.
      return outcome ? withEvidence(outcome, 'record_only') : outcome;
    }

    case 'managed_composite': {
      // ENG-6139: managed toolkits — run the MCP handshake AND the connected-
      // account binding check, return the worse outcome so a green handshake
      // never masks a dead/mis-bound account. Each capability is optional: an
      // older manager (or a target missing inputs) falls back to whichever runs,
      // and `null` only when neither is available.
      const outcomes: ConnectivityProbeOutcome[] = [];
      if (deps.mcpProbe) {
        outcomes.push(
          withEvidence(
            await deps.mcpProbe({
              serverKey: target.mcpServerKey ?? target.definitionId,
              definitionId: target.definitionId,
              inlineServerEntry: target.inlineServerEntry,
              ...rowFacts,
            }),
            // ENG-8226: initialize + tools/list. The transport answered; no
            // authenticated operation ran. The dwight `needs_reauth` rows all
            // had healthy handshakes — this is why it is not enough for green.
            'handshake',
          ),
        );
      }
      if (deps.composioProbe) {
        outcomes.push(
          withEvidence(
            await deps.composioProbe(target.mcpServerKey ?? target.definitionId, target.credentials, target.inlineServerEntry, rowFacts),
            'record_only',
          ),
        );
      }
      // ENG-6157 (Phase 2): the deepest leg — a real read-only tool call. Skips
      // (`null`) when no safe tool is callable, so it only ever ADDS signal,
      // never a false negative.
      if (deps.composioToolCallProbe) {
        const toolCall = await deps.composioToolCallProbe({
          serverKey: target.mcpServerKey ?? target.definitionId,
          definitionId: target.definitionId,
          inlineServerEntry: target.inlineServerEntry,
          ...rowFacts,
          // ENG-6242: thread the prescribed tool through to the live tool-call
          // leg. resolveConnectivityProbe only sets these for managed toolkits
          // with a stored override; the probe re-validates read-only and falls
          // back to auto-pick on drift, so a missing/invalid override is safe.
          toolName: descriptor.probeTool ?? null,
          toolArgs: descriptor.probeArgs ?? null,
        });
        // ENG-8226: the deepest leg — a genuine read-only tool call against the
        // provider. This is the ONLY leg that earns `live_call`.
        if (toolCall) outcomes.push(withEvidence(toolCall, 'live_call'));
      }
      if (outcomes.length === 0) return null;
      // ENG-8226: statuses fold WORST-wins (unchanged, ENG-6139); evidence folds
      // BEST-wins. Both are right: if the live tool call failed, the verdict is
      // `down` AND the evidence is `live_call` — we did make the call.
      const worst = outcomes.reduce((acc, o) => worseConnectivityOutcome(acc, o));
      const evidence = outcomes.reduce<ConnectivityEvidence>(
        (acc, o) => bestConnectivityEvidence(acc, o.evidence ?? 'none'),
        'none',
      );
      // Assign unconditionally, NOT via withEvidence: every leg is already
      // stamped, so `worst.evidence` is always truthy and withEvidence would
      // no-op, silently discarding the fold above. That inverted the whole point
      // — when all three legs pass, worseConnectivityOutcome's tie-break returns
      // the first-pushed leg (the handshake), so a fully-verified integration
      // would report `handshake` and render `unverified`. (CodeRabbit on #3865.)
      return { ...worst, evidence };
    }

    case 'mcp_tools_list': {
      if (!deps.mcpProbe) return null;
      const outcome = await deps.mcpProbe({
        serverKey: target.mcpServerKey ?? target.definitionId,
        definitionId: target.definitionId,
        inlineServerEntry: target.inlineServerEntry,
        ...rowFacts,
        // ENG-7077: the toolkit's prescribed read-only tool, when it declares
        // one. Mirrors the mcp_stdio branch below — these two legs had drifted,
        // and only the stdio one could earn `live_call`.
        toolName: descriptor.probeTool ?? null,
        toolArgs: descriptor.probeArgs ?? null,
      });
      // ENG-8226 grading, same rule as the stdio sibling: a prescribed tool
      // means this leg actually CALLED something and the credential worked, so
      // it is a live_call. Without one it remains a handshake — which cannot
      // support a green badge, by design.
      return withEvidence(outcome, descriptor.probeTool ? 'live_call' : 'handshake');
    }

    case 'mcp_stdio': {
      // ENG-7405: local-STDIO MCP server (origami, …). Spawn the wired server
      // and handshake; call the read-only `connectivity_test` tool when the
      // resolver surfaced one (descriptor.probeTool). No `mcpStdioProbe` wired
      // (older manager / test without it) → skip, don't invent a status.
      if (!deps.mcpStdioProbe) return null;
      const outcome = await deps.mcpStdioProbe({
        serverKey: target.mcpServerKey ?? target.definitionId,
        definitionId: target.definitionId,
        inlineServerEntry: target.inlineServerEntry,
        ...rowFacts,
        toolName: descriptor.probeTool ?? null,
        toolArgs: descriptor.probeArgs ?? null,
      });
      // ENG-8226: a prescribed `connectivity_test` tool means this leg actually
      // called something; without one it is a handshake only.
      return withEvidence(outcome, descriptor.probeTool ? 'live_call' : 'handshake');
    }

    case 'cli_command': {
      if (!deps.runCli) return null;
      const outcome = await deps.runCli(target.cliBinary ?? target.definitionId, descriptor.cliArgs ?? ['--version']);
      // ENG-8226: the default `--version` proves the binary exists, not that its
      // credentials work — `handshake`. A toolkit that stored a real auth check
      // as its connectivity_test override (e.g. `gh auth status`) earns
      // `live_call`.
      //
      // ENG-5463: that was the intent, and the condition could not deliver it.
      // It graded on `descriptor.cliArgs` being SET, but `cliArgsFor` never
      // returns undefined — it falls back to `['--version']` — so EVERY CLI
      // probe was graded `live_call`, the bare version check included, in
      // direct contradiction of the comment above it. `live_call` is the only
      // grade `evidenceSupportsOk` admits, so a toolkit with revoked
      // credentials rendered green on the strength of its binary existing.
      // The resolver now states which of the two it actually resolved.
      return withEvidence(outcome, descriptor.cliArgsAreAuthCheck ? 'live_call' : 'handshake');
    }

    case 'builtin':
      // ENG-8226 criterion 4 — THE fake green. This returned a hardcoded
      // `status: 'ok'` having contacted nothing at all, and that value was
      // persisted into `last_connectivity_status` and rendered as a green
      // "reachable" chip, indistinguishable from a verified live tool call. Any
      // toolkit with source_type='native' that isn't an HTTP provider and has no
      // mcpUrl lands here, so a whole class of integrations reported healthy
      // without a single byte leaving the host.
      //
      // The honest answer is `unverified`: we have no evidence either way. It is
      // deliberately not `down` (nothing observed a failure) and deliberately
      // not `ok`. A real per-module check is still the follow-up; until then the
      // data no longer claims something it never checked.
      return {
        status: 'unverified',
        message: `${target.definitionId}: built-in module — no live check available, not verified`,
        evidence: 'none',
      };

    case 'host_unprobeable':
      // ENG-8316 — no network call, but a REAL outcome rather than null.
      //
      // Returning null here (what `unsupported` does) drops the row from the
      // report batch, and a row that is never reported never reaches
      // `applyConnectivityReports` — where the server-side handling for these
      // rows lives, including ENG-8205's self-healing clear of stale state.
      // That clear runs "on the next report", and for a dropped row there is no
      // next report. cloud-broker / aws-cli / gcloud froze at `transient_error`
      // from 2026-06-14 and xero-broker reached 495 consecutive failures for
      // precisely that reason: the remedy written for these rows could not see
      // them.
      //
      // `unverified` is already neutral to both hysteresis counters
      // (integration-connectivity-report.ts, ENG-8226) and already in
      // CONNECTIVITY_STATUSES, so nothing downstream needs to change to accept
      // it.
      //
      // Use the resolver's own label rather than a hardcoded string. There are
      // TWO broker families here and they fail differently: a cloud broker
      // mints credentials per task, an APPROVAL broker (xero-broker) holds no
      // credential at all and authenticates server-side. Hardcoding the first
      // wording would have persisted a message that is simply untrue for the
      // second - which is the same "assert something you did not check" habit
      // this ticket exists to fix (CodeRabbit).
      return {
        status: 'unverified',
        message: descriptor.label,
        evidence: 'none',
      };

    case 'unsupported':
    default:
      return null;
  }
}

/**
 * ENG-8226: stamp a probe leg's outcome with what that leg actually did.
 *
 * Applied at the executor rather than inside each probe implementation so the
 * evidence classification lives in ONE readable place next to the dispatch that
 * chose the strategy — the alternative (each probe self-reporting) is how the
 * `status_message` grammars fragmented across eight producers in the first place.
 * An explicit `evidence` already on the outcome wins, so a probe that knows
 * better can still say so.
 */
function withEvidence(
  outcome: ConnectivityProbeOutcome,
  evidence: ConnectivityEvidence,
): ConnectivityProbeOutcome {
  return outcome.evidence ? outcome : { ...outcome, evidence };
}
